import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  EMPTY_PORTFOLIO_KPIS,
  EXPANDED_BAR_CHART_OPTION,
  INIT_CHART_OPTION_BIG,
  SORT_ORDER
} from '@catalogues';
import { ExpandedViewComponent } from '@components';
import {
  Kpi,
  KpiCategories,
  KpiKeys,
  KpisByKey,
  PortfolioKpiResponse,
  SortOrder,
  Study
} from '@models';
import {
  FilterClientState,
  FilterState,
  SidebarService,
  StudyFilterService,
  StudyService
} from '@services';
import { columnConfigurations, parseToLineChartData } from '@utils';
import { sortByCustomOrder, sortStandard } from '@utils';
import { catchError, debounceTime, map, of, takeUntil, tap } from 'rxjs';
import { Subject } from 'rxjs';

import { ClientSwitcherDropdownComponent } from './components/client-switcher-dropdown/client-switcher-dropdown.component';
const PAGINATION_SIZE = 10;
type EmailProvider = 'gmail' | 'outlook' | 'default';

interface MailtoPayload {
  to: string;
  subject: string;
  body: string;
}

@Component({
  selector: 'study-portfolio',
  templateUrl: './study-portfolio.component.html',
  styleUrl: './study-portfolio.component.scss'
})
export class StudyPortfolioComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private kpiRequest$ = new Subject<void>();
  private studyService: StudyService = inject(StudyService);
  private studyFilterService: StudyFilterService = inject(StudyFilterService);
  private sidebarService: SidebarService = inject(SidebarService);
  private cdr = inject(ChangeDetectorRef);
  private standardClientNames: string[] = [];
  pageTitle = 'My portfolio';
  expandedChartTitle = 'Studies by therapeutic area';
  hasData = true;
  isLoading = true;
  isKpiLoading = true;
  hasError = false;
  columnConfigurations = columnConfigurations;
  allColumns: (keyof Study)[] = Object.keys(columnConfigurations) as (keyof Study)[];
  displayedColumns: (keyof Study)[] = [];
  sponsorNames = this.studyService.getClientNamesList();
  moreThanOneSponsor: boolean = this.sponsorNames.length > 1;
  sponsorSelected = sessionStorage.getItem('sponsorSelected') || 'All';

  switchOpen = false;
  pendingSponsor = '';
  @ViewChild('sponsorDd') sponsorDd!: ClientSwitcherDropdownComponent;
  private updateDisplayedColumns(): void {
    const clientNamesList = this.studyService.getClientNamesList();
    if (clientNamesList.length > 1) {
      this.displayedColumns = [...this.allColumns];
    } else {
      this.displayedColumns = this.allColumns.slice(1);
    }
  }

  columnTitleMapping: Record<string, string> = Object.fromEntries(
    Object.entries(columnConfigurations).map(([key, config]) => [key, config.displayName])
  );

  sourceData!: Study[];
  currentStudiesDisplayed = 0;
  displayedData: Study[] = [];
  tableData = new MatTableDataSource<Study>([]);
  sidebarCollapsed!: boolean;
  sortedStudiesByTherapeuticArea: any = {};
  sortedStudiesByPhase: any = {};
  sortedStudiesByStage: any = {};
  sortedStudiesByAccelerator: any = {};
  currentSort: Sort = { active: 'protocolNumber', direction: 'asc' };
  totalFiltersApplied = 0;
  currentPage = 1;
  resultsPerPage = [10];
  activeResultsPerPage = 10;
  totalResults = 50;
  showFilterMenu = false;
  showHeaderMenu = false;
  filterMenuPosition = { top: 55, left: 85 };
  headerMenuPosition = { top: 55, left: 85 };
  paginationVariants = JSON.stringify({
    dropdown: true,
    indicator: true,
    goto: false
  });
  currentSortColumn: string | null = null;
  currentSortDirection: 'asc' | 'desc' | null = null;
  activeMenuColumn: string | null = null;
  kpiCardTitle: KpiCategories = 'Portfolio KPIs';
  kpis: KpisByKey = {};
  selectedKpiKey?: KpiKeys;
  tooltipsEnabled = true;
  dialog = inject(MatDialog);
  chartData$: any;
  showModal = false;
  trendText = 'since 30 days ago';
  showProviderModal = false;
  emailPayload: MailtoPayload | null = null;
  showPaginator = true;
  @ViewChild('paginator', { read: ElementRef }) paginatorRef!: ElementRef;
  countryTooltipElement: Study | null = null;
  protocolTooltipOpen = false;
  protocolTooltipElement: Study | null = null;
  private protocolTooltipHideTimeoutId: any = null;
  private isProtocolAnchorHovered = false;
  private isProtocolTooltipHovered = false;

  getElementValue(element: Study, column: string): any {
    return element[column as keyof Study] ?? '';
  }

  openHeaderMenu() {
    this.headerMenuPosition = { top: 55, left: 260 };
    this.showHeaderMenu = true;
  }

  closeMenu() {
    this.activeMenuColumn = null;
    this.showHeaderMenu = false;
  }

  toggleCountryTooltip(element: Study): void {
    this.countryTooltipElement = this.countryTooltipElement === element ? null : element;
  }

  onCountryTooltipChange(isOpen: boolean, element: Study): void {
    this.countryTooltipElement = isOpen ? element : null;
  }

  openSwitcherConfirmationModal(pending?: string): void {
    this.onDropdownApply(pending ?? this.sponsorSelected);
  }

  onDropdownApply(pending: string): void {
    this.pendingSponsor = pending;
    this.switchOpen = true;
  }

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    if (ms) {
      // Initialize the sort from current values if they exist
      if (this.currentSort.active && this.currentSort.direction) {
        ms.active = this.currentSort.active;
        ms.direction = this.currentSort.direction;
        this.currentSortColumn = this.currentSort.active as string;
        this.currentSortDirection = this.currentSort.direction;
      }

      ms.sortChange.subscribe((sort: Sort) => {
        this.currentSort = sort;
        this.currentSortColumn = sort.active as string;
        this.currentSortDirection = sort.direction as 'asc' | 'desc' | null;
        this.applySortAndPagination();
      });
    }
  }

  async ngOnInit() {
    this.updateDisplayedColumns();

    this.standardClientNames = this.studyService.getStandardClientNames();

    // Initialize sort tracking properties
    if (this.currentSort.active && this.currentSort.direction) {
      this.currentSortColumn = this.currentSort.active as string;
      this.currentSortDirection = this.currentSort.direction;
    }

    let previousFilterCount = 0;
    let previousClientFilter: FilterClientState = { active: false, client: '' };
    let initialKpiLoaded = false;

    this.studyFilterService.filterState$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((filterState: FilterState) => {
        this.displayedData = filterState.filteredData;
        this.tableData.data = filterState.filteredData;
        this.totalResults = filterState.filteredData.length;
        this.totalFiltersApplied = filterState.totalFilterCount;
        this.resetPaginatorUI();

        this.hasData = filterState.filteredData.length > 0;

        if (filterState.filteredData.length < PAGINATION_SIZE) {
          this.resultsPerPage = [filterState.filteredData.length];
          this.activeResultsPerPage = filterState.filteredData.length;
        } else {
          this.resultsPerPage = [PAGINATION_SIZE];
          this.activeResultsPerPage = PAGINATION_SIZE;
        }

        this.applySortAndPagination();
        this.updateChartData(filterState.filteredData);

        if (!initialKpiLoaded) {
          this.updateKpiInfo([], true);
          initialKpiLoaded = true;
        } else if (previousFilterCount !== filterState.totalFilterCount) {
          this.updateKpiInfo(filterState.filteredData);
        }

        previousFilterCount = filterState.totalFilterCount;
      });

    this.studyFilterService.filterClientState$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((filterClientState: FilterClientState) => {
        const clientFilterChanged =
          previousClientFilter.active !== filterClientState.active ||
          previousClientFilter.client !== filterClientState.client;

        if (filterClientState.active) {
          // When client filter is active, count studies for that specific client from source data
          const clientStudies =
            this.sourceData?.filter((study) => study.clientName === filterClientState.client) || [];
          this.currentStudiesDisplayed = clientStudies.length;
        } else {
          // When client filter is inactive, show total source data count
          this.currentStudiesDisplayed = this.sourceData?.length || 0;
        }

        if (clientFilterChanged && initialKpiLoaded) {
          // When client filter changed and initial kpi is loaded, update KPIs
          const currentState = this.studyFilterService.getCurrentState();
          this.updateKpiInfo(currentState.filteredData);
        }

        previousClientFilter = { ...filterClientState };
      });

    this.loadStudiesData();

    this.sidebarService.sidebarCollapsed$.subscribe((collapsed: boolean) => {
      this.sidebarCollapsed = collapsed;
    });
  }

  private loadStudiesData(): void {
    this.studyService
      .getStudiesPortfolio(this.standardClientNames)
      .pipe(
        tap((response: Study[]) => {
          this.sourceData = structuredClone(response);
          this.displayedData = structuredClone(this.sourceData);
          this.currentStudiesDisplayed = this.displayedData.length;

          this.sourceData.forEach((study: Study) => {
            if (study.country != null) {
              const countriesList = study.country.split(',').map((country) => country.trim());
              study.countryList = countriesList;
              study.country = countriesList.length > 1 ? 'Multiple' : countriesList[0];
            }

            if (study.region != null) {
              const regionsList = study.region.split(',').map((region) => region.trim());
              study.regionList = regionsList;
              study.region = regionsList.length > 1 ? 'Multiple' : regionsList[0];
            }
          });

          this.studyFilterService.initializeFilters(this.sourceData);

          // Check sessionStorage and apply client filter after data is loaded
          this.applySessionStorageSponsorSelection();

          this.applySortAndPagination();
          this.isLoading = false;
        }),
        catchError((err: any) => {
          console.error('Error fetching studies portfolio', err);
          this.isLoading = false;
          this.hasData = false;
          this.hasError = true;
          return of(null);
        })
      )
      .subscribe();
  }

  private applySessionStorageSponsorSelection(): void {
    const savedSponsor = sessionStorage.getItem('sponsorSelected');
    if (savedSponsor && savedSponsor !== 'All') {
      this.sponsorSelected = savedSponsor;
      // Apply the client filter after data is loaded
      this.studyFilterService.addClientFilter(true, savedSponsor);
    } else {
      this.sponsorSelected = 'All';
      // Ensure client filter is cleared
      this.studyFilterService.removeClientFilter();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.kpiRequest$.complete();
    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
    }
  }

  updateChartData(data: Study[]): void {
    const chartData = parseToLineChartData(
      'therapeuticArea',
      data,
      ({ therapeuticArea }: Study) => therapeuticArea
    );

    if (chartData['NULL']) {
      delete chartData['NULL'];
    }

    this.sortedStudiesByTherapeuticArea = chartData;

    this.sortedStudiesByPhase = parseToLineChartData(
      'studyPhase',
      data,
      ({ studyPhaseSimpleLabel }: Study) => studyPhaseSimpleLabel
    );

    this.sortedStudiesByStage = parseToLineChartData(
      'studyStage',
      data,
      ({ studyStage }: Study) => studyStage
    );

    const studiesByAccelerator = Object.groupBy(data, ({ accelerator }) => accelerator);
    const acceleratorEntries = Object.entries(studiesByAccelerator);
    acceleratorEntries.sort(([, arrA], [, arrB]) => (arrA?.length || 0) - (arrB?.length || 0));
    this.sortedStudiesByAccelerator = Object.fromEntries(acceleratorEntries);
  }

  updateKpiInfo(data: Study[], isInitialLoad = false): void {
    this.isKpiLoading = true;
    this.kpiRequest$.next();

    let clientNamesToUse = this.standardClientNames;
    let filteredStudies = data;
    let studySkList: string[] = [];

    if (this.sponsorSelected !== 'All') {
      clientNamesToUse = [this.sponsorSelected];
      filteredStudies = data.filter((study: Study) => study.clientName === this.sponsorSelected);
    }

    if (isInitialLoad) {
      studySkList = ['all'];
    } else if (this.totalFiltersApplied === 0 && this.sponsorSelected === 'All') {
      studySkList = ['all'];
    } else if (filteredStudies.length > 0) {
      studySkList = filteredStudies.map((study: Study) => study.studySk);
    } else {
      studySkList = [];
    }

    this.studyService
      .getPortfolioKpi(studySkList, clientNamesToUse)
      .pipe(
        takeUntil(this.kpiRequest$),
        takeUntil(this.destroy$),
        catchError((err: any) => {
          console.error('Error fetching KPIs:', err);
          this.kpis = { ...EMPTY_PORTFOLIO_KPIS };
          this.isKpiLoading = false;
          return of(null);
        })
      )
      .subscribe((response: PortfolioKpiResponse | null) => {
        if (response) {
          console.log('Received KPIs:', response);
          this.kpis = {};
          const kpisByKey = response.kpis;

          Object.entries(kpisByKey).forEach(([key, kpi]: [string, Kpi]) => {
            this.kpis[key as KpiKeys] = kpi;
            kpi.trendText = this.trendText;
          });
        } else {
          this.kpis = { ...EMPTY_PORTFOLIO_KPIS };
        }

        this.isKpiLoading = false;
      });
  }

  onPageChange(event: CustomEvent) {
    const nextPage = event.detail.activePage;
    if (nextPage !== this.currentPage) {
      this.currentPage = nextPage;
      this.applySortAndPagination();
    }
  }

  private applySortAndPagination(): void {
    let displayData = [...this.displayedData];

    if (this.currentSort.active && this.currentSort.direction) {
      const column = this.currentSort.active as keyof Study;
      const ascending = this.currentSort.direction === 'asc';

      this.currentSortColumn = column as string;
      this.currentSortDirection = this.currentSort.direction;
      const sortOrderEntry = this.getSortOrderEntry(column as string);

      if (sortOrderEntry?.alphabetical) {
        displayData = sortStandard(displayData, column, ascending);
      } else {
        const customOrder = sortOrderEntry
          ? ascending
            ? sortOrderEntry.sortingAscending
            : sortOrderEntry.sortingDescending
          : [];

        if (customOrder && customOrder.length > 0) {
          displayData = sortByCustomOrder(displayData, column, customOrder);
        } else {
          displayData = sortStandard(displayData, column, ascending);
        }
      }
    }

    const startIndex = (this.currentPage - 1) * PAGINATION_SIZE;
    const endIndex = Math.min(startIndex + PAGINATION_SIZE, displayData.length);
    this.totalResults = displayData.length;
    this.tableData.data = displayData.slice(startIndex, endIndex);
  }

  private getSortOrderEntry(columnType: string): SortOrder | undefined {
    return SORT_ORDER.find((order) => order.type.toLowerCase() === columnType.toLowerCase());
  }

  contextmenuFocusOut() {
    this.showHeaderMenu = false;
  }

  onMenuSort(direction: 'asc' | 'desc', column: string) {
    this.activeMenuColumn = null;
    if (this.currentSortColumn === column && this.currentSortDirection === direction) {
      //Reset to default sort if sorting direction is selected twice on the same column
      this.currentSort = { active: 'protocolNumber', direction: 'asc' };
      this.currentSortColumn = 'protocolNumber';
      this.currentSortDirection = 'asc';
    } else {
      this.currentSort = { active: column as keyof Study, direction };
      this.currentSortColumn = column;
      this.currentSortDirection = direction;
    }
    this.applySortAndPagination();
    this.showHeaderMenu = false;
  }

  toggleMenu(event: MouseEvent, columnId: string) {
    event.stopPropagation();
    if (this.activeMenuColumn === columnId) {
      this.activeMenuColumn = null;
    } else {
      this.activeMenuColumn = columnId;
    }
  }

  openExpandedView() {
    this.dialog.open(ExpandedViewComponent, {
      data: {
        viewTitle: this.expandedChartTitle,
        viewDescription: ' ',
        chartOption: EXPANDED_BAR_CHART_OPTION,
        initOption: INIT_CHART_OPTION_BIG,
        sortedData$: this.studyFilterService.filterState$.pipe(
          map((filterState: FilterState) => {
            const chartData = parseToLineChartData(
              'therapeuticArea',
              filterState.filteredData,
              ({ therapeuticArea }) => therapeuticArea
            );
            return chartData;
          })
        ),
        showAllKeys: true,
        field: 'therapeuticArea',
        filterService: this.studyFilterService,
        chartType: 'default'
      },
      panelClass: 'fullscreen-dialog',
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw'
    });
  }

  onSponsorSwitchModalClosed(modalSelection: 'yes' | 'no' | 'cancel') {
    this.switchOpen = false;
    if (modalSelection === 'yes') {
      this.sponsorSelected = this.pendingSponsor;

      this.studyFilterService.removeClientFilter();
      if (this.pendingSponsor !== 'All')
        this.studyFilterService.addClientFilter(true, this.pendingSponsor);
    }

    this.sponsorDd?.close();
  }

  onFeedbackModalClosed(modalSelection: 'yes' | 'no' | 'cancel'): void {
    this.showModal = false;
    if (modalSelection === 'yes') {
      this.showProviderModal = true;
    }
  }

  onFeedbackOpenRequested(payload: MailtoPayload): void {
    this.emailPayload = {
      to: (payload.to ?? '').trim(),
      subject: payload.subject ?? '',
      body: payload.body ?? ''
    };
    this.showModal = true;
  }

  onProviderSelected(provider: EmailProvider | 'cancel'): void {
    this.showProviderModal = false;
    if (!this.emailPayload || provider === 'cancel') return;

    const { to, subject, body } = this.emailPayload;
    const su = encodeURIComponent(subject);
    const bo = encodeURIComponent(body);
    const toParam = encodeURIComponent(to);

    let url = '';

    switch (provider) {
      case 'gmail':
        url = `https://mail.google.com/mail/u/0/?fs=1&tf=cm&to=${toParam}&su=${su}&body=${bo}`;
        break;
      case 'outlook':
        url = `https://outlook.office.com/mail/deeplink/compose?to=${toParam}&subject=${su}&body=${bo}`;
        break;
      case 'default':
        url = `mailto:${to}?subject=${su}&body=${bo}`;
        break;
    }

    window.open(url, '_blank', 'noopener');
  }

  getKpiKey(stringKey: string): KpiKeys {
    return stringKey as KpiKeys;
  }

  handleKpiSelection(kpiKey: KpiKeys): void {
    this.selectedKpiKey = this.selectedKpiKey === kpiKey ? undefined : kpiKey;
  }

  private remountPaginatorAndSet(): void {
    this.showPaginator = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.showPaginator = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        const el = this.paginatorRef?.nativeElement as any;
        if (!el) return;
        el.activepage = this.currentPage;
        (el as HTMLElement).setAttribute('activepage', String(this.currentPage));
        this.cdr.detectChanges();
      }, 0);
    }, 0);
  }
  private resetPaginatorUI(): void {
    this.currentPage = 1;
    this.remountPaginatorAndSet();
  }
  onProtocolMouseEnter(element: Study): void {
    this.isProtocolAnchorHovered = true;

    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    this.protocolTooltipElement = element;
    this.protocolTooltipOpen = true;
  }

  onProtocolMouseLeave(): void {
    this.isProtocolAnchorHovered = false;
    this.protocolTooltipHide();
  }

  onProtocolTooltipHover(): void {
    this.isProtocolTooltipHovered = true;

    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }
  }

  onProtocolTooltipLeave(): void {
    this.isProtocolTooltipHovered = false;
    this.protocolTooltipHide();
  }

  private protocolTooltipHide(): void {
    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    if (this.isProtocolAnchorHovered || this.isProtocolTooltipHovered) {
      return;
    }

    this.protocolTooltipHideTimeoutId = window.setTimeout(() => {
      this.protocolTooltipOpen = false;
      this.protocolTooltipHideTimeoutId = null;
    }, 150);
  }
}
